import Footer from '@react/shared/components/Footer';
import Header from '@react/shared/components/Header/Header.container';
import { default as Image } from '@react/shared/components/Image';
import { formatDateV2 as formatDate } from '@sly/core/helpers/date';
import cx from 'classnames';
import dynamic from 'next/dynamic';
import { StrictMode, useEffect } from 'react';
import HubDynamicContent from 'sly/hubPage/components/HubDynamicContent';
import { HubPageProps } from 'sly/hubPage/components/HubPage/HubPage.types';
import { assetPath } from 'sly/lib/path';
import { useParams } from 'sly/lib/react-router';
import BlockHr from 'sly/resourceCenter/components/BlockHr';
import Helmet from 'sly/resourceCenter/components/Helmet';
import ReviewedByBlock from 'sly/resourceCenter/components/ReviewedByBlock';
import { getAuthorPath, getReviewerPath } from 'sly/resourceCenter/helpers';
import { pageNames, useEvents } from 'sly/services/events';
import Link from 'sly/system/Link';

import HubHeader from '../HubHeader';
const AuthorPreview = dynamic(
  /* #__LOADABLE__ */ () =>
    import(
      /* webpackChunkName: "authorPreview" */ 'sly/resourceCenter/components/AuthorPreview'
    )
);

function HubPage({ initialApiState }: HubPageProps) {
  const { getHubPage } = initialApiState;
  const data = getHubPage[Object.keys(getHubPage)[0]];
  const { hub } = useParams();
  const { body, hasFinished } = data;
  const hubPageData = body?.data?.[0] || {};
  const {
    subTitle,
    mainImg,
    title,
    description,
    content,
    reviewer,
    author,
    updatedAt,
    reviewedOn,
    publishedAt,
  } = hubPageData;
  const { events } = useEvents();
  useEffect(() => {
    events.page(pageNames.Hub, {});
  }, []);

  if (!hasFinished) {
    return (
      <div
        className='flex h-[100vh] items-center justify-center'
        data-tp-id='gen-div-be923ec0-90a8-4c30-8686-91f146930291'
      >
        <Image
          src={assetPath('uploads/react-assets/homebase/loader.svg')}
          alt='loading'
          data-tp-id='gen-Image-437e070e-c018-41db-b668-c55d46c8e031'
        />
      </div>
    );
  }

  const { fullName: reviewerName, slug: reviewerSlug } = reviewer || {};

  return (
    <StrictMode>
      <Helmet
        imgUrl={mainImg?.url}
        isHubPage
        path={`/${hub}`}
        shortDescription={description}
        {...hubPageData}
        published_at={publishedAt ? formatDate(publishedAt) : null}
        updated_at={updatedAt ? formatDate(updatedAt) : null}
      />
      <header>
        <Header />
      </header>
      <HubHeader imagePath={mainImg?.path} heading={title} label={subTitle} />

      {(author || (reviewerName && reviewedOn)) && (
        <div className='m-auto flex w-full flex-col px-xxl pt-xl md:w-[var(--col6)] md:px-0 lg:w-[var(--col8)]'>
          {author && (
            <div
              className='font-b-s text-slate-lighter-40'
              data-tp-id='gen-div-d8b77650-6fd1-476a-8f01-1afda292a24c'
            >
              By{' '}
              <Link href={getAuthorPath(author?.slug)}>{author?.fullName}</Link>{' '}
              Updated on {formatDate(updatedAt)}
            </div>
          )}

          {reviewerName && reviewedOn && (
            <div className='font-b-s mt-2 text-slate-lighter-40'>
              Reviewed by{' '}
              <Link href={getReviewerPath(reviewerSlug)}>{reviewerName}</Link> ·
              Reviewed on {formatDate(reviewedOn)}
            </div>
          )}
        </div>
      )}

      <HubDynamicContent content={content} />

      {author && (
        <div
          className={cx(
            'mx-m md:my-0 md:mx-auto md:w-[31.5rem] lg:w-[42.5rem]',
            !reviewer && '!mb-20'
          )}
          data-tp-id='gen-div-4f6fa69f-7f75-4851-abd6-1207d747efcc'
        >
          <AuthorPreview
            path={author?.img?.path}
            shortDescription={author?.shortDescription}
            alternativeText={author?.img?.alternativeText}
            fullName={author?.fullName}
            firstName={author?.firstName}
            slug={author?.slug}
          />
        </div>
      )}
      {reviewer && (
        <>
          <BlockHr />
          <div className='mx-m mb-xxl md:mx-auto md:w-[31.5rem] lg:w-[42.5rem]'>
            <ReviewedByBlock reviewer={reviewer} />
            <BlockHr />
          </div>
        </>
      )}
      <Footer />
    </StrictMode>
  );
}

export default HubPage;
